import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Check, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SpecialtyPicker — categorised, multi-select chip picker for instructor specialisms.
 *
 * Props:
 *  - value:    string[]   — currently selected specialty labels
 *  - onChange: (next: string[]) => void
 *  - max:      number      — defaults to backend's MAX_SPECIALTIES_PER_INSTRUCTOR
 *  - testidPrefix: string  — defaults to "specialty"
 *
 * Behaviour:
 *  - Loads /api/marketplace/specialty-groups once
 *  - Tap chip toggles selection. When at max, untoggled chips are disabled until user removes one.
 *  - Sticky header shows X/MAX with subtle warning past max-1.
 */
export default function SpecialtyPicker({
  value = [],
  onChange,
  max,
  testidPrefix = "specialty",
}) {
  const [groups, setGroups] = useState([]);
  const [serverMax, setServerMax] = useState(5);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/marketplace/specialty-groups`, { withCredentials: true });
        if (cancelled) return;
        setGroups(data.groups || []);
        setServerMax(data.max_per_instructor || 5);
      } catch {
        /* fail open — user just won't see the picker */
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const limit = max ?? serverMax;
  const selectedSet = useMemo(() => new Set(value || []), [value]);
  const atLimit = selectedSet.size >= limit;

  const toggle = (s) => {
    const next = new Set(selectedSet);
    if (next.has(s)) next.delete(s);
    else if (next.size < limit) next.add(s);
    else return; // ignore — over limit
    onChange?.(Array.from(next));
  };

  if (!loaded) {
    return (
      <div className="text-xs text-slate-400 py-3">Loading specialisms…</div>
    );
  }

  return (
    <div data-testid={`${testidPrefix}-picker`} className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">
          Specialisms
        </p>
        <span
          data-testid={`${testidPrefix}-count`}
          className={`text-[11px] font-extrabold tabular-nums ${
            atLimit ? "text-amber-600" : "text-slate-400"
          }`}
        >
          {selectedSet.size} / {limit}
        </span>
      </div>

      {groups.map((g) => (
        <div key={g.key} data-testid={`${testidPrefix}-group-${g.key}`}>
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 mb-1.5">
            {g.label}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {g.specialties.map((s) => {
              const on = selectedSet.has(s);
              const disabled = !on && atLimit;
              return (
                <button
                  key={s}
                  type="button"
                  data-testid={`${testidPrefix}-chip-${slug(s)}`}
                  data-state={on ? "on" : "off"}
                  onClick={() => toggle(s)}
                  disabled={disabled}
                  className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition ${
                    on
                      ? "bg-violet-600 text-white border-violet-600 shadow-sm"
                      : disabled
                      ? "bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-slate-400 cursor-not-allowed"
                      : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-violet-400"
                  }`}
                >
                  {on ? <Check className="w-3 h-3 inline -mt-0.5 mr-0.5" strokeWidth={3} /> : null}
                  {s}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {atLimit && (
        <p className="text-[11px] text-amber-600 dark:text-amber-300 font-bold inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3" />
          {limit} chosen. Pick fewer for sharper matching — quality beats quantity.
        </p>
      )}
    </div>
  );
}

function slug(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
